import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import {
  DiversityKPIs,
  CertificationSpend,
  DiversityTrendPoint,
  CategoryDiversitySpend,
  GeographySpend,
  TopDiverseVendor,
  DiversityExportData,
} from '@/hooks/useDiversityAnalytics';

interface DiversityExportButtonProps {
  startDate: Date;
  endDate: Date;
  selectedCategories: string[];
  selectedCertifications: string[];
  kpis: DiversityKPIs | undefined;
  certifications: CertificationSpend[] | undefined;
  trends: DiversityTrendPoint[] | undefined;
  categories: CategoryDiversitySpend[] | undefined;
  geography: GeographySpend[] | undefined;
  vendors: TopDiverseVendor[] | undefined;
  jobs: DiversityExportData['jobs'] | undefined;
  isLoading: boolean;
}

function formatCurrency(cents: number): string {
  return (cents / 100).toFixed(2);
}

function formatPercentage(value: number): string {
  return value.toFixed(2);
}

function escapeCSV(value: any): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function generateCSV(
  startDate: Date,
  endDate: Date,
  selectedCategories: string[],
  selectedCertifications: string[],
  kpis: DiversityKPIs | undefined,
  certifications: CertificationSpend[] | undefined,
  trends: DiversityTrendPoint[] | undefined,
  categories: CategoryDiversitySpend[] | undefined,
  geography: GeographySpend[] | undefined,
  vendors: TopDiverseVendor[] | undefined,
  jobs: DiversityExportData['jobs'] | undefined
): string {
  const lines: string[] = [];

  // Header
  lines.push('DIVERSITY SPEND ANALYTICS REPORT');
  lines.push(`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`);
  lines.push(`Date Range: ${format(startDate, 'yyyy-MM-dd')} to ${format(endDate, 'yyyy-MM-dd')}`);
  if (selectedCategories.length > 0) {
    lines.push(`Categories Filter: ${selectedCategories.join(', ')}`);
  }
  if (selectedCertifications.length > 0) {
    lines.push(`Certifications Filter: Applied`);
  }
  lines.push('');

  // Summary Metrics
  lines.push('=== SUMMARY METRICS ===');
  if (kpis) {
    lines.push(`Total Spend,$${formatCurrency(kpis.totalSpendCents)}`);
    lines.push(`Diversity Spend,$${formatCurrency(kpis.diverseSpendCents)}`);
    lines.push(`Diversity Spend %,${formatPercentage(kpis.diverseSpendPercentage)}%`);
    lines.push(`Diverse Jobs,${kpis.diverseJobCount}`);
    lines.push(`Total Jobs,${kpis.totalJobCount}`);
    lines.push(`Diverse Vendors,${kpis.diverseVendorCount}`);
    lines.push(`Total Vendors,${kpis.totalVendorCount}`);
    lines.push(`Diverse Vendor %,${formatPercentage(kpis.diverseVendorPercentage)}%`);
    lines.push(`Win Rate,${formatPercentage(kpis.diverseWinRate)}%`);
    lines.push(`Bids Submitted,${kpis.diverseBidsSubmitted}`);
    lines.push(`Jobs Awarded,${kpis.diverseJobsAwarded}`);
  }
  lines.push('');

  // Spend by Certification
  lines.push('=== SPEND BY CERTIFICATION TYPE ===');
  lines.push('Certification,Category,Spend,% of Diversity,% of Total,Jobs,Vendors');
  if (certifications) {
    for (const cert of certifications) {
      lines.push([
        escapeCSV(cert.certificationName),
        escapeCSV(cert.categoryName),
        `$${formatCurrency(cert.spendCents)}`,
        `${formatPercentage(cert.percentageOfDiverseSpend)}%`,
        `${formatPercentage(cert.percentageOfTotalSpend)}%`,
        cert.jobCount,
        cert.vendorCount,
      ].join(','));
    }
  }
  lines.push('');

  // Trend Data
  lines.push('=== DIVERSITY TREND OVER TIME ===');
  lines.push('Period,Diverse Spend,Total Spend,Diversity %,Diverse Jobs,Total Jobs');
  if (trends) {
    for (const trend of trends) {
      lines.push([
        escapeCSV(trend.period),
        `$${formatCurrency(trend.diverseSpendCents)}`,
        `$${formatCurrency(trend.totalSpendCents)}`,
        `${formatPercentage(trend.diversePercentage)}%`,
        trend.diverseJobCount,
        trend.totalJobCount,
      ].join(','));
    }
  }
  lines.push('');

  // Category Breakdown
  lines.push('=== SPEND BY CATEGORY ===');
  lines.push('Category,Diverse Spend,Non-Diverse Spend,Total Spend,Diversity %,Diverse Jobs,Total Jobs');
  if (categories) {
    for (const cat of categories) {
      lines.push([
        escapeCSV(cat.categoryLabel),
        `$${formatCurrency(cat.diverseSpendCents)}`,
        `$${formatCurrency(cat.nonDiverseSpendCents)}`,
        `$${formatCurrency(cat.totalSpendCents)}`,
        `${formatPercentage(cat.diversePercentage)}%`,
        cat.diverseJobCount,
        cat.totalJobCount,
      ].join(','));
    }
  }
  lines.push('');

  // Geography Breakdown
  lines.push('=== SPEND BY GEOGRAPHY ===');
  lines.push('State/Region,Diverse Spend,% of Diversity,Vendors,Jobs');
  if (geography) {
    for (const geo of geography) {
      lines.push([
        escapeCSV(geo.state),
        `$${formatCurrency(geo.diverseSpendCents)}`,
        `${formatPercentage(geo.percentageOfDiverseSpend)}%`,
        geo.diverseVendorCount,
        geo.jobCount,
      ].join(','));
    }
  }
  lines.push('');

  // Vendor Details
  lines.push('=== TOP DIVERSE VENDORS ===');
  lines.push('Vendor Name,Certifications,Jobs Awarded,Total Spend,% of Diversity,Win Rate,Avg Job Value');
  if (vendors) {
    for (const vendor of vendors) {
      lines.push([
        escapeCSV(vendor.vendorName),
        escapeCSV(vendor.certifications.join('; ')),
        vendor.jobsAwarded,
        `$${formatCurrency(vendor.totalSpendCents)}`,
        `${formatPercentage(vendor.percentageOfDiverseSpend)}%`,
        `${formatPercentage(vendor.winRate)}%`,
        `$${formatCurrency(vendor.avgJobValueCents)}`,
      ].join(','));
    }
  }
  lines.push('');

  // Job-Level Detail
  lines.push('=== JOB-LEVEL DETAIL ===');
  lines.push('Job ID,Public ID,Title,Category,Vendor,Certifications,Amount,Award Date');
  if (jobs) {
    for (const job of jobs) {
      lines.push([
        escapeCSV(job.jobId),
        escapeCSV(job.publicId),
        escapeCSV(job.title),
        escapeCSV(job.category),
        escapeCSV(job.vendorName),
        escapeCSV(job.certifications.join('; ')),
        `$${formatCurrency(job.amountCents)}`,
        job.awardedDate,
      ].join(','));
    }
  }

  return lines.join('\n');
}

export function DiversityExportButton({
  startDate,
  endDate,
  selectedCategories,
  selectedCertifications,
  kpis,
  certifications,
  trends,
  categories,
  geography,
  vendors,
  jobs,
  isLoading,
}: DiversityExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      const csv = generateCSV(
        startDate,
        endDate,
        selectedCategories,
        selectedCertifications,
        kpis,
        certifications,
        trends,
        categories,
        geography,
        vendors,
        jobs
      );

      // Create blob and download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `diversity-report-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  const hasData = kpis || certifications?.length || vendors?.length;

  return (
    <Button
      variant="outline"
      onClick={handleExport}
      disabled={isLoading || isExporting || !hasData}
    >
      {isExporting ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <Download className="h-4 w-4 mr-2" />
      )}
      Export CSV
    </Button>
  );
}
